// Story 7.12 Iteration 2 (ADO-911) — PLACEHOLDER sample values for the Product
// Detail Page.
//
// The PO-approved PDP design (Figma 1169:7024 / 1193:7661) surfaces a large set
// of commercial / master-data / EPOS fields that NO producer exists for yet. Per
// OQ-10 and OQ-13 (both RESOLVED: "ship the hero + accordion display fields as
// FIXTURES") every field below is a DISPLAY-ONLY SEAM, not real data — exactly
// the `productCardSample.ts` posture the grid card already ships.
//
// Deviation D-1: the platform cannot source RRP, margin or offers, so those values
// here are placeholders. The hero's unit price is not one of them: it reads
// `provisionalUnitPrice`, so this fixture carries no price.
//
// The gap register in the story maps each field to the story that will own it:
//   RRP                        → Pricing migration 17 (ADO-2248): real with VITE_REAL_PRICES_ENABLED on
//   Offers / promotions        → Pricing migration 18 (ADO-2249) + Story 11.5 (ADO-1956)
//   WOW                        → Story 11.12 (ADO-1963)
//   POR (Profit on Return)     → Story 11.10 (ADO-1961), G9; DEFINED by the PO 2026-07-29
//   Margin                     → Story 11.10 (ADO-1961), G4
// RPS is GONE: the PO removed it from the designs on 2026-07-29, closing gap G3.
//   Nutrition                  → 14.14 ingest + 7.17 display, G1
//   "Your section"             → Epic 8 section context, G8
// Swap each fixture for the real source as its producer lands.
//
// Story 7.15 (ADO-1372) did exactly that for gaps G5 + G6: Code / EAN / VAT /
// Origin and the breadcrumb category path are REAL now (`ProductDetail` on the
// by-SKU read) and their fixtures are deleted, not merely unused.
//
// Story 7.16 (ADO-1373) did the same for gap G2: the whole `stockCard` sub-object
// — `week`, `soldLast7d`, `depots`, `lastOrderedAgo` — is GONE, along with the
// `StockCardWeekRow` / `DepotStockRow` types, the `depotRow` helper and the
// `DAY_LABELS` export that only the week table used. The week table and the
// "sold (7d)" badge now read Story 14.15's real store EPOS movement
// (`stock-card.client.ts`); the depot rows were DELETED rather than re-sourced,
// because no read model exposes a per-environment breakdown (Deviation D-2), and
// `lastOrderedAgo` went with them — a fabricated "N days ago" has no place beside
// live figures in the same component.
//
// Story 8.12 (ADO-2021) did the same for the hero's On Order row: it reads the
// Customer's open orders (`useOnOrderFor`), and the `onOrder` fixture and its
// type are deleted.
//
// The hero's `lastOrdered` fixture is deleted too (Bug 3737): a made-up date read as
// a real one. Its Last Ordered cell shows the empty-cell glyph until Story 7.23
// (ADO-1712) wires the real date.

export interface ProductDetailSample {
  // ── Hero (D-1: no producer) ──
  readonly rrp: string;
  readonly packCase: string;
  /** POR (`P.P`, no `%`; see the note on `por` in `productDetailSample`), the end of the hero's
   *  price line in both flag states (ADO-2249). Still computed from the sample RRP and price. */
  readonly porPct: string;
  readonly marginPct: string;
  readonly offer: { readonly label: string; readonly window: string } | null;
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

// Hoisted, not constructed per call: `Intl.NumberFormat` is expensive to build
// and this module is called from every card, tile and modal render.
const EUR = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' });
const eur = (n: number): string => EUR.format(n);

const DAY_MS = 86_400_000;

/** TODAY at UTC midnight. Anchoring the date fixtures here (rather than to a
 *  frozen 2025-01-01 epoch) is what keeps them from contradicting the live
 *  chrome around them: a fixed epoch rendered every ordering window inside
 *  January 2025 while a green "N days left" pill claimed it was still open, and
 *  put a "15/01/2025" last-ordered date beside a "9 days ago" caption. Stable
 *  within a day, which is all the fixtures need; UTC-normalised, so it is not
 *  the bare-`new Date()` anti-pattern. */
function todayUtcMidnight(): number {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

/** Deterministic dd/mm/yyyy, `offsetDays` from today (negative = in the past). */
function sampleDate(offsetDays: number): string {
  const d = new Date(todayUtcMidnight() + offsetDays * DAY_MS);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

/**
 * Deterministic placeholder PDP data for a SKU (stable across renders, and
 * stable across runs — no `Date.now()`, no randomness). Mirrors
 * `sampleCardData` so the PDP and the grid card tell the same story for a SKU.
 */
export function productDetailSample(sku: string): ProductDetailSample {
  const h = hash(sku);
  // The placeholder buying price the RRP and POR below are derived from (same basis
  // as the card's RRP). Not returned: the hero shows the provisional unit price.
  const price = 1.5 + (h % 1600) / 100; // €1.50–€17.49
  const marginPct = 6 + (h % 1100) / 100;
  const rrp = price * (1 + marginPct / 100 + 0.02);
  const packQty = 3 + (h % 10);
  // Placeholder pack WEIGHT. Derived once and used by BOTH the hero's RRP line
  // and its Pack/Case cell — `packCase` previously interpolated the SKU here, so
  // the cell read "7 / 58384" on any product whose read-model row has no
  // `packSize` (the only case where the fixture is reached at all).
  const packWeightG = 800 + (h % 5) * 50;
  const hasOffer = h % 4 === 0;
  // PO definition (2026-07-29): POR = Profit on Return — the retailer's CASH
  // margin as a percentage of the SELLING price (not of cost), the standard
  // Irish/UK grocery metric. Distinct from `marginPct`, which is the cost-based
  // margin SL1.0 shows on the product tile. `rrp` stands in for the shelf price
  // and `price` for the buying price, which is the only pair the fixtures have —
  // a placeholder shape, not a real calculation. Producer: Story 11.10 (ADO-1961, gap G9).
  // Reaches the page only as the price line's trailing `· 12.6%` (Figma 1169:7301), as `porPct`
  // in both flag states (ADO-2249).
  const por = ((rrp - price) / rrp) * 100;

  return {
    rrp: eur(rrp),
    packCase: `${packQty} / ${packWeightG}g`,
    porPct: por.toFixed(1),
    marginPct: marginPct.toFixed(2),
    offer: hasOffer
      ? {
          label: `Buy 4+ — save ${eur(0.5 + (h % 150) / 100)}`,
          window: `${sampleDate(-2)} – ${sampleDate(26)}`,
        }
      : null,
    // Story 7.15 (ADO-1372): `categoryPath` and `productInformation` are GONE from
    // this fixture — the breadcrumb (G6) and Code / EAN / VAT / Origin (G5) are now
    // REAL read-model data, sourced through `ProductDetail` on the by-SKU read. The
    // hashed `Code` in particular was WRONG DATA rather than placeholder data: it is
    // the value a customer quotes to order, and it is simply the SKU (D-2).
    //
    // The `Available` window went with them, and does NOT come back here: the PO's
    // 2026-07-30 answer scopes the ordering window to products ON OFFER, and SL1.0's
    // own Start/End rows are `promStart`/`promEnd` — a promotion window. It belongs
    // to Story 11.5 (#1109) with its offer, not to Product Information (D-3).
  };
}
